import React, { useCallback } from "react";
import { connect } from "react-redux";

import Radio from "antd/es/radio";
import Space from "antd/es/space";
import { useIntl } from "react-intl";

import RadioGroup from "@app/component/input/RadioGroup";
import RangePicker from "@app/component/input/RangePicker";
import { smartPlannerViewAnalytics } from "@app/scene/activity/activityTableAnalytics";
import { RootState } from "@app/store/rootReducer";

import DateRangeType, { DATE_RANGES, getDateRangeTypeDisplayName } from "../../enum/DateRangeType";
import { getRange, getRangeType } from "../../store";
import { setDateRange } from "../../store/actions";

import styles from "./Setup.module.scss";

interface Props {
  onChangeDateRange: (payload: {
    range?: { end: Date; start: Date };
    rangeType: DateRangeType;
  }) => void;
  range?: { end: Date; start: Date };
  rangeType: DateRangeType;
}

const DateRangeSelector: React.FC<Props> = ({ onChangeDateRange, range, rangeType }) => {
  const intl = useIntl();

  const handleChangeType = useCallback(
    (rangeType) => {
      onChangeDateRange({
        range,
        rangeType,
      });
      smartPlannerViewAnalytics?.using(["Build routes from activities", "Date Range selector"], {
        dateRange: getDateRangeTypeDisplayName(intl, rangeType),
      });
    },
    [intl, onChangeDateRange, range]
  );

  const handleChangeCustomRange = useCallback(
    (dates: [Date | null, Date | null] | null) => {
      if (!dates || !dates[0] || !dates[1]) {
        return;
      }
      onChangeDateRange({
        range: { end: dates[1], start: dates[0] },
        rangeType: DateRangeType.CUSTOM,
      });
    },
    [onChangeDateRange]
  );

  return (
    <div className={styles.selector}>
      <span>
        {intl.formatMessage({
          id: "activities.calendarView.routeBuilder.steps.setup.dateRangeSelector.title",
          defaultMessage: "Create route for",
          description: "Title of date range selector in Route Builder in Activities Calendar view",
        })}
      </span>
      <RadioGroup onChange={handleChangeType} value={rangeType}>
        <Space direction="vertical">
          {DATE_RANGES.map((rangeType) => (
            <Radio key={rangeType} value={rangeType}>
              {getDateRangeTypeDisplayName(intl, rangeType)}
            </Radio>
          ))}
        </Space>
      </RadioGroup>
      {rangeType === DateRangeType.CUSTOM && (
        <RangePicker
          className={styles.rangePicker}
          format="PP"
          onChange={handleChangeCustomRange}
          size="large"
          value={[range?.start ?? null, range?.end ?? null]}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  range: getRange(state),
  rangeType: getRangeType(state),
});

const mapDispatchToProps = {
  onChangeDateRange: setDateRange,
};

export default connect(mapStateToProps, mapDispatchToProps)(DateRangeSelector);
